import React, { useState } from 'react';
import {
  BarChart3, TrendingUp, PieChart, Activity, Calendar, 
  Download, Filter, Eye, RefreshCw, DollarSign, Users,
  Package, Clock, Target, Award, TrendingDown, AlertTriangle, Search, Plus
} from 'lucide-react';

interface ReportData {
  id: string;
  name: string;
  type: 'sales' | 'inventory' | 'patient' | 'prescription' | 'financial';
  lastGenerated: string;
  status: 'ready' | 'generating' | 'failed';
  size: string;
  format: 'pdf' | 'excel' | 'csv';
}

interface ChartData {
  month: string;
  prescriptions: number;
  revenue: number;
  patients: number;
  inventory: number;
}

interface MetricCard {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'stable';
  icon: any;
  color: string;
}

const mockReports: ReportData[] = [
  {
    id: 'RPT-001',
    name: 'Monthly Sales Report',
    type: 'sales',
    lastGenerated: '2024-01-15',
    status: 'ready',
    size: '2.4 MB',
    format: 'pdf'
  },
  {
    id: 'RPT-002',
    name: 'Inventory Status Report',
    type: 'inventory',
    lastGenerated: '2024-01-14',
    status: 'ready',
    size: '1.8 MB',
    format: 'excel'
  },
  {
    id: 'RPT-003',
    name: 'Patient Demographics',
    type: 'patient',
    lastGenerated: '2024-01-13',
    status: 'ready',
    size: '3.2 MB',
    format: 'csv'
  },
  {
    id: 'RPT-004',
    name: 'Prescription Analytics',
    type: 'prescription',
    lastGenerated: '2024-01-12',
    status: 'generating',
    size: '--',
    format: 'pdf'
  },
  {
    id: 'RPT-005',
    name: 'Financial Summary Q4',
    type: 'financial',
    lastGenerated: '2024-01-10',
    status: 'ready',
    size: '4.1 MB',
    format: 'excel'
  }
];

const mockChartData: ChartData[] = [
  { month: 'Jan', prescriptions: 1250, revenue: 45600, patients: 890, inventory: 12500 },
  { month: 'Feb', prescriptions: 1380, revenue: 50200, patients: 920, inventory: 11800 },
  { month: 'Mar', prescriptions: 1420, revenue: 51800, patients: 950, inventory: 11200 },
  { month: 'Apr', prescriptions: 1350, revenue: 49200, patients: 880, inventory: 12000 },
  { month: 'May', prescriptions: 1480, revenue: 54200, patients: 980, inventory: 10800 },
  { month: 'Jun', prescriptions: 1520, revenue: 55600, patients: 1010, inventory: 10500 }
];

const mockMetrics: MetricCard[] = [
  {
    title: 'Total Revenue',
    value: '$298,600',
    change: '+12.5%',
    trend: 'up',
    icon: DollarSign,
    color: 'green'
  },
  {
    title: 'Active Patients',
    value: '1,010',
    change: '+8.2%',
    trend: 'up',
    icon: Users,
    color: 'blue'
  },
  {
    title: 'Prescriptions',
    value: '8,400',
    change: '+15.3%',
    trend: 'up',
    icon: Package,
    color: 'purple'
  },
  {
    title: 'Inventory Value',
    value: '$125,400',
    change: '-2.1%',
    trend: 'down',
    icon: Package,
    color: 'orange'
  }
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'ready': return 'bg-green-100 text-green-800 border-green-200';
    case 'generating': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'failed': return 'bg-red-100 text-red-800 border-red-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getTypeColor = (type: string) => {
  switch (type) {
    case 'sales': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'inventory': return 'bg-green-100 text-green-800 border-green-200';
    case 'patient': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'prescription': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'financial': return 'bg-red-100 text-red-800 border-red-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getTrendIcon = (trend: string) => {
  switch (trend) {
    case 'up': return <TrendingUp className="w-4 h-4 text-green-600" />;
    case 'down': return <TrendingDown className="w-4 h-4 text-red-600" />;
    case 'stable': return <Target className="w-4 h-4 text-gray-600" />;
    default: return <Target className="w-4 h-4 text-gray-600" />;
  }
};

const getTrendColor = (trend: string) => {
  switch (trend) {
    case 'up': return 'text-green-600';
    case 'down': return 'text-red-600';
    case 'stable': return 'text-gray-600';
    default: return 'text-gray-600';
  }
};

export function Reports() {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredReports = mockReports.filter(report => {
    const matchesSearch = report.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || report.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || report.status === statusFilter;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const totalReports = mockReports.length;
  const readyReports = mockReports.filter(r => r.status === 'ready').length;
  const generatingReports = mockReports.filter(r => r.status === 'generating').length;
  const failedReports = mockReports.filter(r => r.status === 'failed').length;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#4B0082] via-[#6B38FB] to-purple-600 rounded-3xl p-10 text-white shadow-2xl shadow-[#6B38FB]/25">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center space-x-4 mb-6">
            <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30">
              <BarChart3 className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-4xl font-black mb-2">Reports & Analytics</h1>
              <p className="text-xl text-purple-100">Data-driven insights and comprehensive reporting</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Total Reports</p>
              <p className="text-2xl font-black">{totalReports}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Ready</p>
              <p className="text-2xl font-black text-green-300">{readyReports}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Generating</p>
              <p className="text-2xl font-black text-yellow-300">{generatingReports}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Failed</p>
              <p className="text-2xl font-black text-red-300">{failedReports}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {mockMetrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div key={index} className="group relative bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:shadow-[#6B38FB]/20 transition-all duration-500 hover:scale-105 hover:-translate-y-2 overflow-hidden">
              {/* Hover Effect Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#6B38FB]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Animated Border */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#4B0082] via-[#6B38FB] to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-2xl bg-${metric.color}-100 text-${metric.color}-600 group-hover:scale-110 transition-transform duration-500 shadow-lg`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className={`flex items-center text-xs font-semibold ${getTrendColor(metric.trend)}`}>
                    {getTrendIcon(metric.trend)}
                    <span className="ml-1">{metric.change}</span>
                  </div>
                </div>
                <p className="text-3xl font-black text-gray-900 mb-2 group-hover:text-[#4B0082] transition-colors duration-500">{metric.value}</p>
                <p className="text-sm font-semibold text-gray-600 group-hover:text-[#6B38FB] transition-colors duration-500">{metric.title}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Chart Section */}
      <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Performance Overview</h3>
            <p className="text-gray-600">Monthly trends and key performance indicators</p>
          </div>
          <div className="flex space-x-3">
            <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Refresh
            </button>
            <button className="px-4 py-2 bg-[#6B38FB] text-white rounded-lg hover:bg-[#4B0082] transition-colors">
              <Download className="w-4 h-4 inline mr-2" />
              Export
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Prescriptions Chart */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-900">Prescriptions Trend</h4>
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div className="space-y-3">
              {mockChartData.slice(-3).map((data, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{data.month}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-blue-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-1000"
                        style={{ width: `${(data.prescriptions / 1600) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{data.prescriptions.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Revenue Chart */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-900">Revenue Trend</h4>
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div className="space-y-3">
              {mockChartData.slice(-3).map((data, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{data.month}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-green-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full transition-all duration-1000"
                        style={{ width: `${(data.revenue / 60000) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">${(data.revenue / 1000).toFixed(0)}k</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
            >
              <option value="all">All Types</option>
              <option value="sales">Sales</option>
              <option value="inventory">Inventory</option>
              <option value="patient">Patient</option>
              <option value="prescription">Prescription</option>
              <option value="financial">Financial</option>
            </select>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
            >
              <option value="all">All Statuses</option>
              <option value="ready">Ready</option>
              <option value="generating">Generating</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          
          <button className="px-6 py-3 bg-gradient-to-r from-[#4B0082] to-[#6B38FB] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#6B38FB]/25 transition-all duration-300 hover:scale-105 flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>New Report</span>
          </button>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#4B0082]/5 to-[#6B38FB]/5">
          <h3 className="text-xl font-bold text-gray-900">Available Reports</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Report Name</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Generated</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Size</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Format</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredReports.map((report) => (
                <tr key={report.id} className="hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900">{report.name}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getTypeColor(report.type)}`}>
                      {report.type.charAt(0).toUpperCase() + report.type.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{report.lastGenerated}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(report.status)}`}>
                      {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{report.size}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 uppercase">{report.format}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                        <Download className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors">
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Custom Reports</h4>
              <p className="text-sm text-gray-600">Create personalized analytics</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Performance Insights</h4>
              <p className="text-sm text-gray-600">Track key metrics</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Activity className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Real-time Data</h4>
              <p className="text-sm text-gray-600">Live dashboard updates</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
